import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { BackButton, HomeButton } from '../components/NavButtons';
import SearchableSelect from '../components/SearchableSelect';
import VARIANT_CONFIG from '../config/variantConfig';
import axios from 'axios';
import { Country, State, City } from 'country-state-city';
import { getCurrencySymbol, getCurrencyCode, CURRENCY_OPTIONS } from '../utils/currency';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { useInactiveCountries } from '../hooks/useInactiveCountries';
import {
  ArrowLeft, Plus, X, Check, MapPin, Trash, Camera, Lightning,
  FloppyDisk, Rocket, Image, VideoCamera, Tag, Percent, CurrencyDollar
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  CATEGORIES, AMENITIES, PRICING_MODELS, HOURS,
  Section
} from './addListing/_helpers';
import { LocationRow } from './addListing/LocationRow';
import { VariantTable } from './addListing/VariantTable';
import { CouponsSection } from './addListing/CouponsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AddListing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: urlId } = useParams();
  const { user, isSeller } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draftId, setDraftId] = useState(urlId || searchParams.get('draft_id') || null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [listingStatus, setListingStatus] = useState(null);

  const [formData, setFormData] = useState({
    category: '', title: '', short_description: '', description: '',
    address: '', google_map_link: '', landmark: '', pincode: '', entry_instructions: '',
    amenities: [], capacity: '',
    pricing_model: 'hourly', deposit: '', cleaning_fee: '', currency: '',
    cancellation_policy: 'flexible', rules: [],
    open_time: '00:00', close_time: '23:59', instant_booking: true,
  });

  const [variants, setVariants] = useState([]);
  const [sharedFields, setSharedFields] = useState({});
  const [locations, setLocations] = useState([{ countryCode: '', country: '', stateCode: '', state: '', city: '', pincode: '' }]);
  const [imageUrls, setImageUrls] = useState(['']);
  const [newRule, setNewRule] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [expandedVariantMedia, setExpandedVariantMedia] = useState(null);
  const platformSettings = usePlatformSettings();
  const [intlCurrency, setIntlCurrency] = useState('USD');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/platform/international-currency`);
        if (!cancelled) setIntlCurrency(res.data?.international_currency || 'USD');
      } catch (e) { if (!cancelled) console.error('Failed to load international currency, using USD default', e); }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleAmenity = (amenity) => setFormData(prev => ({
    ...prev, amenities: prev.amenities.includes(amenity)
      ? prev.amenities.filter(a => a !== amenity) : [...prev.amenities, amenity]
  }));

  const addRule = () => { if (newRule.trim()) { setFormData(prev => ({ ...prev, rules: [...prev.rules, newRule.trim()] })); setNewRule(''); } };
  const removeRule = (i) => setFormData(prev => ({ ...prev, rules: prev.rules.filter((_, idx) => idx !== i) }));

  const addImageUrl = () => setImageUrls(prev => [...prev, '']);
  const updateImageUrl = (i, val) => { const u = [...imageUrls]; u[i] = val; setImageUrls(u); };
  const removeImageUrl = (i) => { const u = imageUrls.filter((_, idx) => idx !== i); setImageUrls(u.length ? u : ['']); };

  const addLocation = () => setLocations(prev => [...prev, { countryCode: '', country: '', stateCode: '', state: '', city: '', pincode: '' }]);
  const updateLocation = (i, data) => setLocations(prev => prev.map((l, idx) => idx === i ? data : l));
  const removeLocation = (i) => setLocations(prev => prev.filter((_, idx) => idx !== i));

  // Coupon management
  const addCoupon = () => setCoupons(prev => [...prev, { code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_discount: '', valid_from: '', valid_to: '' }]);
  const updateCoupon = (i, field, val) => setCoupons(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const removeCoupon = (i) => setCoupons(prev => prev.filter((_, idx) => idx !== i));

  const initVariants = useCallback((category, existingVariants) => {
    const config = VARIANT_CONFIG[category];
    if (!config) { setVariants([]); setSharedFields({}); return; }

    if (existingVariants && existingVariants.length > 0) {
      setVariants(existingVariants);
    } else {
      const initial = config.variantOptions.map(opt => {
        const row = { enabled: false, [config.variantField]: opt, regular_price: '', holiday_price: '', media: [] };
        config.columns.forEach(col => { row[col.key] = col.type === 'toggle' ? false : ''; });
        return row;
      });
      setVariants(initial);
    }

    if (config.sharedFields) {
      const shared = {};
      config.sharedFields.forEach(f => { shared[f.key] = f.type === 'multiselect' ? [] : ''; });
      setSharedFields(shared);
    } else {
      setSharedFields({});
    }
  }, []);

  useEffect(() => {
    if (formData.category && !draftLoaded) {
      initVariants(formData.category);
    }
  }, [formData.category, initVariants, draftLoaded]);

  // Auto-select category from URL query param (e.g. ?category=yoga_wellness)
  useEffect(() => {
    if (draftId) return; // don't override drafts
    const cat = searchParams.get('category');
    if (cat && CATEGORIES.some(c => c.id === cat) && !formData.category) {
      setFormData(prev => ({ ...prev, category: cat }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, draftId]);

  useEffect(() => {
    const loadDraft = async () => {
      if (!draftId || draftLoaded) return;
      try {
        const res = await axios.get(`${API_URL}/api/listings/${draftId}`);
        const d = res.data;
        setFormData({
          category: d.category || '', title: d.title || '', short_description: d.short_description || '',
          description: d.description || '', address: d.address || '', google_map_link: d.google_map_link || '',
          landmark: d.landmark || '', pincode: d.pincode || '', entry_instructions: d.entry_instructions || '',
          amenities: d.amenities || [], capacity: d.capacity || '',
          pricing_model: d.pricing_model || 'hourly', deposit: d.deposit || '', cleaning_fee: d.cleaning_fee || '',
          currency: d.currency || '', cancellation_policy: d.cancellation_policy || 'flexible', rules: d.rules || [],
          open_time: d.open_time || '00:00', close_time: d.close_time || '23:59', instant_booking: d.instant_booking !== false,
        });
        if (d.locations && d.locations.length > 0) {
          setLocations(d.locations.map(l => ({
            countryCode: l.countryCode || '', country: l.country || '',
            stateCode: l.stateCode || '', state: l.state || '', city: l.city || '', pincode: l.pincode || ''
          })));
        }
        if (d.images && d.images.length > 0) setImageUrls(d.images);
        if (d.coupons && d.coupons.length > 0) setCoupons(d.coupons);
        if (d.category_specific?.variants) {
          initVariants(d.category, d.category_specific.variants);
        } else if (d.category) {
          initVariants(d.category);
        }
        if (d.category_specific?.shared) setSharedFields(d.category_specific.shared);
        setListingStatus(d.status || null);
        setDraftLoaded(true);
      } catch (err) {
        console.error('Failed to load draft:', err);
        setDraftLoaded(true);
      }
    };
    loadDraft();
  }, [draftId, draftLoaded, initVariants]);

  const updateVariant = (index, key, value) => {
    setVariants(prev => prev.map((v, i) => i === index ? { ...v, [key]: value } : v));
  };

  const toggleSharedField = (key, option) => {
    setSharedFields(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(option) ? arr.filter(x => x !== option) : [...arr, option] };
    });
  };

  const config = VARIANT_CONFIG[formData.category];
  const currencyCode = formData.currency || getCurrencyCode(user?.country);
  const currencySymbol = CURRENCY_OPTIONS.find(c => c.code === currencyCode)?.symbol || getCurrencySymbol(user?.country);

  const enabledVariants = variants.filter(v => v.enabled);
  const minPrice = enabledVariants.length > 0
    ? Math.min(...enabledVariants.map(v => parseFloat(v.regular_price) || 0).filter(p => p > 0))
    : 0;

  const buildPayload = (isDraft) => {
    const variantData = variants.map(v => {
      const { enabled, ...rest } = v;
      return { ...rest, enabled };
    });
    const validLocations = locations.filter(l => l.country || l.state || l.city || l.pincode).map(l => ({
      countryCode: l.countryCode, country: l.country, state: l.state, city: l.city, pincode: l.pincode
    }));
    return {
      title: formData.title || (isDraft ? 'Untitled Draft' : ''),
      description: formData.description || (isDraft ? 'Draft' : ''),
      short_description: formData.short_description || null,
      category: formData.category || (isDraft ? 'hotels' : ''),
      address: formData.address || (isDraft ? 'Draft' : ''),
      google_map_link: formData.google_map_link || null,
      landmark: formData.landmark || null,
      pincode: formData.pincode || '',
      entry_instructions: formData.entry_instructions || null,
      images: imageUrls.filter(u => u.trim()),
      amenities: formData.amenities,
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
      pricing_model: formData.pricing_model,
      base_price: minPrice > 0 ? minPrice : (isDraft ? 0 : 0),
      deposit: formData.deposit ? parseFloat(formData.deposit) : null,
      cleaning_fee: formData.cleaning_fee ? parseFloat(formData.cleaning_fee) : null,
      cancellation_policy: formData.cancellation_policy,
      rules: formData.rules,
      open_time: formData.open_time,
      close_time: formData.close_time,
      instant_booking: formData.instant_booking,
      category_specific: {
        variants: variantData,
        ...(Object.keys(sharedFields).length > 0 ? { shared: sharedFields } : {})
      },
      currency_override: formData.currency || null,
      locations: validLocations,
      services: [],
      coupons: coupons.filter(c => c.code.trim()),
    };
  };

  const handleSaveDraft = async () => {
    if (!formData.category) { setError('Please select a category first'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = buildPayload(true);
      if (draftId) {
        payload.listing_id = draftId;
      }
      const res = await axios.post(`${API_URL}/api/listings/draft`, payload);
      setDraftId(res.data.listing_id);
      toast.success('Draft saved successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save draft';
      setError(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally { setSaving(false); }
  };

  const handlePublish = async () => {
    setError('');
    const fail = (m) => { setError(m); toast.error(m); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    if (!formData.category) { fail('Please select a category'); return; }
    if (!formData.title) { fail('Please enter a listing title'); return; }
    if (!formData.description) { fail('Please enter a description'); return; }
    if (!formData.address) { fail('Please enter an address'); return; }
    if (enabledVariants.length === 0) { fail('Please enable at least one variant in the configuration table'); return; }
    const prices = enabledVariants.map(v => parseFloat(v.regular_price)).filter(p => !isNaN(p) && p > 0);
    if (prices.length === 0) { fail('Please set a regular price for at least one enabled variant'); return; }

    setLoading(true);
    try {
      const payload = buildPayload(false);
      payload.base_price = Math.min(...prices);
      let listingId = draftId;
      if (draftId) {
        await axios.put(`${API_URL}/api/listings/${draftId}`, payload);
        listingId = draftId;
      } else {
        const res = await axios.post(`${API_URL}/api/listings`, payload);
        listingId = res.data.listing_id;
      }
      await axios.post(`${API_URL}/api/listings/${listingId}/publish`, {});
      toast.success('Listing published!');
      navigate('/seller/listings');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to publish listing';
      const finalMsg = typeof msg === 'object' ? JSON.stringify(msg) : msg;
      setError(finalMsg);
      toast.error(finalMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally { setLoading(false); }
  };

  if (!user || !isSeller) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">Become a seller to list spaces</h2>
          <Button onClick={() => navigate('/role-selection')} className="bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid="become-seller-btn">Get Started</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BackButton />
            <HomeButton />
            <h1 className="font-semibold text-slate-900 text-lg">Add Listing</h1>
            {draftId && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium" data-testid="draft-badge">Draft</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={saving || !formData.category}
              className="h-9 rounded-lg text-sm border-slate-300" data-testid="save-draft-btn">
              <FloppyDisk size={16} className="mr-1.5" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button size="sm" onClick={handlePublish} disabled={loading}
              className="h-9 rounded-lg text-sm bg-emerald-500 hover:bg-emerald-600" data-testid="publish-btn">
              <Rocket size={16} className="mr-1.5" />
              {loading ? 'Publishing...' : 'Publish'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-4 space-y-4 pb-8">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium" data-testid="error-message">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-400 hover:text-red-600"><X size={14} /></button>
          </div>
        )}

        {/* ROW 1: Category Selection */}
        <Section title="Select Category" testId="section-category">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isSel = formData.category === cat.id;
              return (
                <button key={cat.id} onClick={() => { updateField('category', cat.id); setDraftLoaded(false); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${isSel ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                  data-testid={`category-${cat.id}`}>
                  <Icon size={14} weight={isSel ? 'fill' : 'regular'} />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </Section>

        {formData.category && (
          <>
            {/* ROW 2: Location & Address */}
            <Section title="Location & Address" testId="section-location">
              <div className="space-y-3">
                <div className="space-y-2">
                  {locations.map((loc, i) => (
                    <LocationRow key={`loc-${loc.countryCode || ''}-${loc.stateCode || ''}-${i}`} loc={loc} index={i}
                      platformSettings={platformSettings} updateLocation={updateLocation}
                      removeLocation={removeLocation} canRemove={locations.length > 1} />
                  ))}
                  <button onClick={addLocation}
                    className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium px-1"
                    data-testid="add-location-btn">
                    <Plus size={14} /> Add location
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Input value={formData.address} onChange={(e) => updateField('address', e.target.value)}
                      placeholder="Full address *" className="h-9 rounded-lg text-sm" data-testid="address-input" />
                  </div>
                  <Input value={formData.pincode} onChange={(e) => updateField('pincode', e.target.value)}
                    placeholder="Pin code" className="h-9 rounded-lg text-sm" data-testid="pincode-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input value={formData.google_map_link} onChange={(e) => updateField('google_map_link', e.target.value)}
                    placeholder="Google Maps link" className="h-9 rounded-lg text-sm" data-testid="maps-link-input" />
                  <Input value={formData.landmark} onChange={(e) => updateField('landmark', e.target.value)}
                    placeholder="Landmark (e.g. Near Metro)" className="h-9 rounded-lg text-sm" data-testid="landmark-input" />
                </div>
              </div>
            </Section>

            {/* ROW 3: Listing Info */}
            <Section title="Listing Info" testId="section-info">
              <div className="space-y-3">
                <Input value={formData.title} onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Listing title *" className="h-9 rounded-lg text-sm" data-testid="title-input" />
                <Input value={formData.short_description} onChange={(e) => updateField('short_description', e.target.value)}
                  placeholder="Short description (one-liner)" className="h-9 rounded-lg text-sm" data-testid="short-desc-input" />
                <Textarea value={formData.description} onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Full description *" className="min-h-[70px] rounded-lg text-sm" data-testid="description-input" />
                <Input value={formData.entry_instructions} onChange={(e) => updateField('entry_instructions', e.target.value)}
                  placeholder="Entry instructions (how to access)" className="h-9 rounded-lg text-sm" data-testid="entry-input" />
              </div>
            </Section>

            {/* ROW 4: Space Configuration Table */}
            {config && (
              <Section title={`Space Configuration - ${CATEGORIES.find(c => c.id === formData.category)?.name}`} testId="section-config">
                <VariantTable
                  config={config} sharedFields={sharedFields} toggleSharedField={toggleSharedField}
                  variants={variants} updateVariant={updateVariant}
                  platformSettings={platformSettings} currencySymbol={currencySymbol}
                  expandedVariantMedia={expandedVariantMedia} setExpandedVariantMedia={setExpandedVariantMedia}
                  enabledVariants={enabledVariants} minPrice={minPrice}
                />
              </Section>
            )}

            {/* ROW 5: Pricing & Schedule */}
            <Section title="Pricing & Schedule" testId="section-pricing">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Currency</Label>
                  <select value={formData.currency || getCurrencyCode(user?.country)}
                    onChange={(e) => updateField('currency', e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-emerald-400 outline-none"
                    data-testid="currency-select">
                    {CURRENCY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                  <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="intl-currency-toggle">
                    <input type="checkbox"
                      checked={(formData.currency || '').toUpperCase() === intlCurrency.toUpperCase()}
                      onChange={(e) => updateField('currency', e.target.checked ? intlCurrency : getCurrencyCode(user?.country))}
                      className="h-3.5 w-3.5 accent-emerald-500" data-testid="intl-currency-checkbox" />
                    <span>Accept in international currency (<strong>{intlCurrency}</strong>)</span>
                  </label>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Pricing Model</Label>
                  <select value={formData.pricing_model} onChange={(e) => updateField('pricing_model', e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-emerald-400 outline-none"
                    data-testid="pricing-model-select">
                    {PRICING_MODELS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Deposit ({currencySymbol})</Label>
                  <Input type="number" value={formData.deposit} onChange={(e) => updateField('deposit', e.target.value)}
                    placeholder="0" className="h-9 rounded-lg text-sm" data-testid="deposit-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Cleaning Fee ({currencySymbol})</Label>
                  <Input type="number" value={formData.cleaning_fee} onChange={(e) => updateField('cleaning_fee', e.target.value)}
                    placeholder="0" className="h-9 rounded-lg text-sm" data-testid="cleaning-fee-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Open Time</Label>
                  <select value={formData.open_time} onChange={(e) => updateField('open_time', e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-emerald-400 outline-none"
                    data-testid="open-time-select">
                    {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Close Time</Label>
                  <select value={formData.close_time} onChange={(e) => updateField('close_time', e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-emerald-400 outline-none"
                    data-testid="close-time-select">
                    {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                    <option value="23:59">11:59 PM</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Cancellation</Label>
                  <select value={formData.cancellation_policy} onChange={(e) => updateField('cancellation_policy', e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-emerald-400 outline-none"
                    data-testid="cancellation-select">
                    <option value="flexible">Flexible</option>
                    <option value="moderate">Moderate</option>
                    <option value="strict">Strict</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button type="button" onClick={() => updateField('instant_booking', !formData.instant_booking)}
                    className={`w-full h-9 flex items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-all ${formData.instant_booking ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'}`}
                    data-testid="instant-booking-toggle">
                    <Lightning size={14} weight={formData.instant_booking ? 'fill' : 'regular'} />
                    Instant Book {formData.instant_booking ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </Section>

            {/* ROW 6: Coupons & Discounts */}
            {platformSettings.seller_coupons !== false && (
            <Section title="Coupons & Discounts" testId="section-coupons">
              <CouponsSection coupons={coupons} updateCoupon={updateCoupon}
                removeCoupon={removeCoupon} addCoupon={addCoupon} currencySymbol={currencySymbol} />
            </Section>
            )}

            {/* ROW 7: Amenities */}
            <Section title="Amenities" testId="section-amenities">
              <div className="flex flex-wrap gap-1.5">
                {AMENITIES.map((amenity) => {
                  const isOn = formData.amenities.includes(amenity);
                  return (
                    <button key={amenity} type="button" onClick={() => toggleAmenity(amenity)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${isOn ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
                      data-testid={`amenity-${amenity}`}>
                      {isOn && <Check size={10} weight="bold" />}
                      {amenity}
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* ROW 8: Images */}
            <Section title="Images" testId="section-images">
              <div className="space-y-2">
                {imageUrls.map((url, i) => (
                  <div key={`img-${i}`} className="flex gap-2 items-center">
                    <Input value={url} onChange={(e) => updateImageUrl(i, e.target.value)}
                      placeholder="https://example.com/photo.jpg" className="h-9 rounded-lg text-sm" data-testid={`image-url-${i}`} />
                    {imageUrls.length > 1 && (
                      <button onClick={() => removeImageUrl(i)} className="p-1.5 text-red-400 hover:text-red-600"><X size={16} /></button>
                    )}
                  </div>
                ))}
                <button onClick={addImageUrl}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium px-1"
                  data-testid="add-image-btn">
                  <Plus size={14} /> Add image URL
                </button>
              </div>
            </Section>

            {/* ROW 9: Rules */}
            <Section title="House Rules" testId="section-rules">
              <div className="flex gap-2">
                <Input value={newRule} onChange={(e) => setNewRule(e.target.value)}
                  placeholder="e.g., No smoking" className="h-9 rounded-lg text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRule())}
                  data-testid="rule-input" />
                <Button type="button" onClick={addRule} size="sm" className="h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs px-4"
                  data-testid="add-rule-btn">Add</Button>
              </div>
              {formData.rules.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.rules.map((rule, i) => (
                    <span key={`${rule}-${i}`} className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-700">
                      {rule}
                      <button onClick={() => removeRule(i)} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </Section>

            {/* Bottom action bar */}
            <div className="flex items-center justify-between gap-4 pt-2">
              <div className="text-xs text-slate-400">
                {enabledVariants.length > 0 && (
                  <span>{enabledVariants.length} variant{enabledVariants.length !== 1 ? 's' : ''} selected</span>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleSaveDraft} disabled={saving || !formData.category}
                  className="h-10 rounded-xl px-6" data-testid="bottom-save-draft-btn">
                  <FloppyDisk size={16} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button onClick={handlePublish} disabled={loading}
                  className="h-10 rounded-xl px-8 bg-emerald-500 hover:bg-emerald-600 font-semibold" data-testid="bottom-publish-btn">
                  <Rocket size={16} className="mr-2" />
                  {loading ? 'Publishing...' : 'Publish Listing'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddListing;
